//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import RiderActions from 'common/redux/actions/rider';
import RiderPersonaActions from 'common/redux/actions/riderPersona';
import ShiftActions from 'common/redux/actions/shiftMaster';
import SalesCategoriesActions from 'common/redux/actions/salesCategories';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import RiderMasterImportContainer from 'common/entity/riderMaster/RiderMasterImportContainer';
import SkillActions from 'common/redux/actions/skill';
import SkillSelectors from 'common/redux/selectors/skill';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import ChannelPreferenceSelectorFilter from 'common/entity/components/ChannelPreferenceSelectorFilter';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import {
    ConnectedSalesCategoryFilter,
    ConnectedSalesCategoryIdFilter
} from 'common/entity/components/SalesCategoryFilter';
import ConnectedRiderPersonaSelectorFilter from 'common/entity/components/RiderPersonaSelectorFilter';
import ConnectedShiftSelectorFilter from 'common/entity/components/ShiftSelectorFilter';
import ToastActions from 'common/redux/actions/toast';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import RiderExportContainer from './RiderExportContainer';
import RiderSelectors from 'common/redux/selectors/rider';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getPlatformSettings } from 'common/modules/platform-settings';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import RiderSlots from 'common/entity/components/RiderSlotSummary';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import HolidaySelector from 'common/entity/generic/HolidaySelector';

import { withRouter } from 'common/components/core/router';
import { withUnitConverter } from 'common/containers/intl';

import SingleRiderEditContainer from './SingleRiderEditContainer';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import theme from 'common/entity/BulkEditContainer.scss';
import specificTheme from './RiderMasterContainer.scss';

import { dashboardColors } from 'common/enums/ColorEnum';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import TeamList from 'common/components/core/common/TeamList';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT,
    DEFAULT_PAGE_SIZE
} from 'common/components/core/table';
import { RiderTypeType } from 'common/models/internal/MasterRider';
import { ConnectedSkillSelectorFilter } from 'common/entity/components/SkillSelectorFilter';
import SkillsList from 'common/components/core/common/SkillsList';

import type { MasterRider } from 'common/models/internal/MasterRider';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import {
    resourceLimitCellFactory,
    getTransactionTimePerTask
} from 'common/entity/components/resourceLimit/ResourceLimitCell';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { Skill } from 'common/models/internal/Skill';
import VisitUtils from 'common/utils/VisitUtils';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/riderMaster';
import { RoleOperationEnum } from 'common/modules/rbac';
import MetricColumnHelper from 'common/entity/helpers/MetricColumnHelper';
import {
    RIDER_RESOURCE_PATH,
    CustomColumnHelpers
} from 'common/entity/helpers/CustomColumnHelpers';

const getLimits = rider => rider.riderPersona?.limits;

const selectTeamId = ({ teamId }) => teamId;

const selectSkillsId = ({ id }) => id;

const getTeamIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { values, allowEmptyOrNull }
    } = filteredColumn;
    const teamValueFilter = values.map(team => ({
        clientId,
        teamId: team
    }));

    return new FilterBuilder()
        .withName('teams')
        .withOperation(FilterOperation.Contains)
        .withValues(teamValueFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const getSalesCategoriesFilter = ({ filteredColumn }) => {
    const {
        value: { values, allowEmptyOrNull }
    } = filteredColumn;
    const salesCategoryFilter = values.map(category => ({
        salesCategoryId: category
    }));

    return new FilterBuilder()
        .withName('pjpData.salesCategoryIds')
        .withOperation(FilterOperation.Contains)
        .withValues(salesCategoryFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

export const DEFAULT_COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick, disabled, theme }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                    disabled={disabled}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'riderTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'riderTable.code',
        id: 'code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'riderTable.name',
        id: 'name',
        accessor: 'name',
        infixSearchable: true
    },
    {
        headerTextId: 'riderTable.phoneNumber',
        accessor: 'phoneNumber.phoneNumber'
    },
    {
        headerTextId: 'riderTable.type',
        accessor: 'type',
        FilterComponent: GeneralFilter,
        getFilterOptions: () => {
            const options = RiderTypeType.getOptions({
                translationKey: 'masterRiderProperties.types'
            });
            return options;
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        headerTextId: 'riderTable.dateOfBirth',
        accessor: 'bioData.dateOfBirth',
        filterable: false,
        minWidth: 100,
        dataType: CellDataTypes.Date
    },
    {
        headerTextId: 'riderTable.email',
        accessor: 'bioData.emailAddress.emailAddress',
        minWidth: 100,
        sortable: true,
        filterable: true
    },
    {
        headerTextId: 'riderTable.personaId',
        accessor: 'riderPersona.id',
        exportId: 'riderPersonaId.riderPersonaId',
        id: 'riderPersonaId',
        isHidden: ({ isFieldHidden }) => isFieldHidden('riderPersona'),
        sortable: true,
        property: 'riderPersonaId.riderPersonaId',
        filterable: true,
        FilterComponent: ConnectedRiderPersonaSelectorFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('riderPersonaId.riderPersonaId')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        }
    },
    {
        headerTextId: 'riderTable.shiftIds',
        accessor: 'shiftsInfo.shiftIds',
        id: 'shiftId',
        sortable: false,
        property: 'shiftsInfo.shiftIds',
        FilterComponent: ConnectedShiftSelectorFilter,
        Cell: props => {
            const { original } = props;
            const shiftIds = original.getIn(['shiftsInfo', 'shiftIds']);
            return shiftIds ? (
                <span>
                    {shiftIds
                        .map(shiftId => shiftId?.shiftId)
                        .toArray()
                        .filter(shiftId => !!shiftId)
                        .join(', ')}
                </span>
            ) : null;
        },
        getCustomFilterInfo: ({ clientId, filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            const filterValues = values.map(value => ({
                clientId,
                shiftId: value
            }));
            return new FilterBuilder()
                .withName('shiftsInfo.shiftIds')
                .withOperation(FilterOperation.Contains)
                .withValues(filterValues)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        }
    },
    {
        headerTextId: 'riderTable.teamName',
        id: 'teams.name',
        exportId: 'teams.name',
        accessor: 'teams',
        sortable: false,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = (original: MasterRider).teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'riderTable.teamId',
        id: 'teams.id',
        exportId: 'teams.id',
        accessor: 'teams',
        sortable: false,
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = (original: MasterRider).teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'riderTable.salesCategoryId',
        exportId: 'pjpData.salesCategories.id',
        id: 'salesCategoryId',
        sortable: false,
        fieldId: 'pjpData.salesCategories',
        FilterComponent: ConnectedSalesCategoryIdFilter,
        getCustomFilterInfo: getSalesCategoriesFilter,
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        accessor: data =>
            data.pjpData &&
            data.pjpData.salesCategories.map(category => category.id).join(', ')
    },
    {
        headerTextId: 'riderTable.salesCategoryName',
        exportId: 'pjpData.salesCategories.name',
        id: 'salesCategoryName',
        sortable: false,
        fieldId: 'pjpData.salesCategories',
        FilterComponent: ConnectedSalesCategoryFilter,
        getCustomFilterInfo: getSalesCategoriesFilter,
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        accessor: data =>
            data.pjpData &&
            data.pjpData.salesCategories
                .map(category => category.name)
                .join(', ')
    },
    {
        headerTextId: 'riderTable.channelPreference',
        id: 'channels',
        accessor: 'pjpData.channels',
        Cell: props => (
            <span>
                {props?.value?.map(channel => channel.name)?.join(', ')}
            </span>
        ),
        sortable: false,
        FilterComponent: ChannelPreferenceSelectorFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values: channels, allowEmptyOrNull }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('pjpData.channels')
                .withOperation(FilterOperation.Contains)
                .withValues(channels)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            return entitySettings.pjpSettings.salesChannels.toJS();
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        headerTextId: 'riderTable.skusSoldCount',
        accessor: 'pjpData.skusSoldCount',
        filterable: false,
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        headerTextId: 'riderTable.zoneIds',
        accessor: 'pjpData.zoneIds',
        infixSearchable: true,
        sortable: false,
        Cell: props => {
            return <span>{props?.value?.join(', ')}</span>;
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        headerTextId: 'riderTable.revenueGenerated',
        accessor: 'pjpData.revenue.amount.amount',
        sortable: false,
        filterable: false,
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        headerTextId: 'riderTable.joiningDate',
        accessor: 'competency.joiningDate',
        filterable: false,
        dataType: CellDataTypes.Date
    },
    {
        headerTextId: 'riderTable.experienceInMonths',
        accessor: 'competency.experience'
    },
    {
        headerTextId: 'riderTable.rating',
        accessor: 'competency.rating.rating',
        filterable: false,
        sortable: false
    },
    {
        headerTextId: 'riderTable.limits.minAllowedLimit',
        translationProps: MetricColumnHelper.getDistanceColumnProps(
            'riderPersonaProperties.limits.distance'
        ),
        id: 'minDistance',
        fieldId: 'riderPersona.limits.distance.lowerThresh',
        property: 'riderPersona.limits.distance.lowerThresh',
        minWidth: 100,
        filterable: false,
        sortable: false,
        Cell: resourceLimitCellFactory('distance', 'lowerThresh', getLimits)
    },
    {
        headerTextId: 'riderTable.limits.maxAllowedLimit',
        translationProps: MetricColumnHelper.getDistanceColumnProps(
            'riderPersonaProperties.limits.distance'
        ),
        id: 'maxDistance',
        fieldId: 'riderPersona.limits.distance.higherThresh',
        property: 'riderPersona.limits.distance.higherThresh',
        Cell: resourceLimitCellFactory('distance', 'higherThresh', getLimits),
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.limits.minAllowedLimit',
        translationProps: MetricColumnHelper.getWeightColumnProps(
            'riderPersonaProperties.limits.weight'
        ),
        id: 'minWeight',
        fieldId: 'riderPersona.limits.weight.lowerThresh',
        property: 'riderPersona.limits.weight.lowerThresh',
        Cell: resourceLimitCellFactory('weight', 'lowerThresh', getLimits),
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.limits.maxAllowedLimit',
        translationProps: MetricColumnHelper.getWeightColumnProps(
            'riderPersonaProperties.limits.weight'
        ),
        id: 'maxWeight',
        fieldId: 'riderPersona.limits.weight.higherThresh',
        property: 'riderPersona.limits.weight.higherThresh',
        Cell: resourceLimitCellFactory('weight', 'higherThresh', getLimits),
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.minTravelTime',
        id: 'minTravelTime',
        fieldId: 'riderPersona.limits.travelTime.lowerThresh',
        property: 'riderPersona.limits.travelTime.lowerThresh',
        Cell: resourceLimitCellFactory('travelTime', 'lowerThresh', getLimits),
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.maxTravelTime',
        id: 'maxTravelTime',
        fieldId: 'riderPersona.limits.travelTime.higherThresh',
        property: 'riderPersona.limits.travelTime.higherThresh',
        Cell: resourceLimitCellFactory('travelTime', 'higherThresh', getLimits),
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.minTask',
        id: 'minTask',
        accessor: 'riderPersona.limits.task.lowerThresh',
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.maxTask',
        id: 'maxTask',
        accessor: 'riderPersona.limits.task.higherThresh',
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.location')
            };
        },
        accessor: 'riderPersona.limits.location.lowerThresh',
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.location')
            };
        },
        accessor: 'riderPersona.limits.location.higherThresh',
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterRiderPersonaProperties.resourceLimits.totalTime')
        }),
        id: 'minTotalTime',
        fieldId: 'riderPersona.limits.totalTime.lowerThresh',
        Cell: resourceLimitCellFactory('totalTime', 'lowerThresh', getLimits),
        sortable: false,
        filterable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterRiderPersonaProperties.resourceLimits.totalTime')
        }),
        id: 'maxTotalTime',
        fieldId: 'riderPersona.limits.totalTime.higherThresh',
        Cell: resourceLimitCellFactory('totalTime', 'higherThresh', getLimits),
        sortable: false,
        filterable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.minVolume',
        id: 'minVolume',
        fieldId: 'riderPersona.limits.volume.lowerThresh',
        property: 'riderPersona.limits.volume.lowerThresh',
        Cell: resourceLimitCellFactory(
            'volume',
            'lowerThresh',
            getLimits,
            true
        ),
        filterable: false,
        sortable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.maxVolume',
        id: 'maxVolume',
        fieldId: 'riderPersona.limits.volume.higherThresh',
        property: 'riderPersona.limits.volume.higherThresh',
        Cell: resourceLimitCellFactory(
            'volume',
            'higherThresh',
            getLimits,
            true
        ),
        sortable: false,
        filterable: false,
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.transactionTimePerTask',
        id: 'transactionTimePerTask',
        fieldId: 'riderPersona.limits.transactionTimePerTask',
        property: 'riderPersona.limits.transactionTimePerTask',
        sortable: false,
        filterable: false,
        accessor: data =>
            getTransactionTimePerTask(
                data.riderPersona?.limits?.transactionTimePerTask
            ),
        minWidth: 100
    },
    {
        headerTextId: 'riderTable.workingDays',
        id: 'workingDays',
        sortable: false,
        filterable: false,
        minWidth: 160,
        fieldId: 'shiftsInfo.workingDays',
        accessor: (data: MasterRider) => {
            const { shiftsInfo } = data;
            if (!shiftsInfo) {
                return '';
            }
            const { workingDays } = shiftsInfo.sortWorkingDays();
            const DaysOfWeekActive = [];
            workingDays.forEach(dayShift => {
                if (dayShift.slot) {
                    DaysOfWeekActive.push(dayShift.getShortCode());
                }
            });
            return DaysOfWeekActive.join(' ');
        },
        Cell: props => {
            const { original: data } = props;
            const { t } = props.column.getHeaderProps();
            const slots = data.shiftsInfo
                ? data.shiftsInfo.initializeWeekDays()
                : data.getDefaultSlots();
            return <RiderSlots riderSlots={slots} title={t('workingHours')} />;
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        id: 'holidays',
        headerTextId: 'riderTable.holidays',
        accessor: 'pjpData.holidays',
        minWidth: 150,
        getFilterOptions: () => {
            return Object.values(DayOfWeekType).map(value => ({
                value,
                label: value
            }));
        },
        Cell: props => {
            const {
                original: { pjpData },
                tdProps: {
                    rest: { t }
                }
            } = props;
            const holidays = pjpData?.holidays || [];
            return (
                <DivWithPopover
                    theme={theme}
                    preferPlace={PopoverPlaceEnum.Right}
                    invertPopover={false}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    popOverContent={
                        <HolidaySelector
                            t={t}
                            disableClick
                            holidays={holidays || []}
                        />
                    }
                >
                    Holidays
                </DivWithPopover>
            );
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        headerTextId: 'riderTable.skills',
        id: 'skills',
        sortable: false,
        fieldId: 'riderPersona.skills',
        accessor: 'riderPersona.skills.name',
        filterable: false,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = original.riderPersona?.skills;
            return skillIds ? (
                <span className={theme.cellText}>
                    {skillIds
                        .map(skillId => {
                            if (!skills) {
                                return skillId.id;
                            }
                            const skill = skills.find(
                                skill =>
                                    skill.clientId === skillId.clientId &&
                                    skill.id === skillId.id
                            );
                            return skill ? skill.name : skillId.id;
                        })
                        .join(', ')}
                </span>
            ) : null;
        }
    },
    {
        headerTextId: 'riderTable.skillId',
        id: 'skills.id',
        sortable: false,
        FilterComponent: ConnectedSkillSelectorFilter,
        accessor: 'riderPersona.skills.id',
        getCustomFilterInfo: ({ clientId, filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            const skills = values.map(skill => ({
                clientId,
                id: skill
            }));
            return new FilterBuilder()
                .withName('competency.skills')
                .withOperation(FilterOperation.Contains)
                .withValues(skills)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = original.riderPersona?.skills;
            return skillIds ? (
                <SkillsList
                    skills={skills}
                    skillIds={skillIds}
                    labelSelector={selectSkillsId}
                />
            ) : null;
        }
    },
    {
        headerTextId: 'riderTable.tatEfficiency',
        accessor: 'riderPersona.competency.tatEfficiency',
        id: 'tatEfficiency',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'riderTable.travelTimeEfficiency',
        accessor: 'riderPersona.competency.travelTimeEfficiency',
        id: 'travelTimeEfficiency',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'riderTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'riderTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'riderTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'riderTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'riderTable.appVersion',
        accessor: 'lotrAppInfo.version',
        filterable: false,
        sortable: false
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type RidersDataType = {
    riders: Array<MasterRider>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: RidersDataType,
    unitConverter: UnitConversionHelper,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    skills: Array<Skill>,
    fetchSkills: ({ clientId: string }) => void,
    fetchSalesCategories: ({ clientId: string }, body: Object) => void,
    fetchRiderPersonas: ({ clientId: string }, body: Object) => void,
    fetchShifts: ({ clientId: string }, body: Object) => void,
    fetchOptions: () => {},
    isGlobalPersonnel: boolean,
    qrEnabled?: boolean
};

const riderMasterContainerFactory = ({
    EntityEditViewContainer = SingleRiderEditContainer,
    EntityImportContainer = RiderMasterImportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            add: RoleOperationEnum.UPDATE_RIDER,
            edit: RoleOperationEnum.UPDATE_RIDER,
            export: RoleOperationEnum.EXPORT_RIDER
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = RiderActions.fetchActiveEntities
}) => {
    const RiderMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer: RiderExportContainer,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class RiderMasterContainer extends PureComponent<Props> {
        componentDidMount = () => {
            this.fetchOtherResources();
        };

        componentDidUpdate = (prevProps: Props) => {
            const { clientId } = this.props;
            if (clientId !== prevProps.clientId) {
                this.fetchOtherResources();
            }
        };

        fetchOtherResources = () => {
            const {
                clientId,
                fetchSkills,
                fetchSalesCategories,
                fetchRiderPersonas,
                fetchShifts
            } = this.props;
            fetchSkills({ clientId });

            const defaultActiveFilter = new FilterListBuilder()
                .add(
                    new FilterBuilder()
                        .withName('status')
                        .withOperation(FilterOperation.Equals)
                        .withValue('ACTIVE')
                )
                .build();

            const paginationConfig = {
                page: 1,
                size: DEFAULT_PAGE_SIZE
            };
            if (clientId) {
                fetchSalesCategories(
                    { clientId },
                    { filters: defaultActiveFilter }
                );
                fetchRiderPersonas({ clientId }, { ...paginationConfig });
                fetchShifts({ clientId }, { ...paginationConfig });
            }
        };

        // Rider persona columns are to be hidden for PJP clients
        areFieldsHidden = (property: string) => {
            switch (property) {
                case 'riderPersona':
                    return !this.props.entitySettings?.riderPersonaSettings
                        .enabled;
                case 'pjp':
                    return !this.props.entitySettings?.pjpSettings.enabled;
                default:
                    return false;
            }
        };

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                unitConverter,
                theme,
                teams,
                skills,
                isGlobalPersonnel
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                unitConverter,
                theme,
                isChecked,
                skills,
                className: classnames({ [theme.selectedRow]: isChecked }),
                teams,
                isGlobalPersonnel
            };
        };

        getAdditionalTableProps = () => {
            const { teams } = this.props;
            return {
                teams
            };
        };

        render() {
            const {
                clientId,
                fetchOptions,
                t,
                qrEnabled,
                entitySettings
            } = this.props;
            const riderFetchOptions = {
                clientId,
                include: [
                    IncludeTypeEnums.Shift,
                    IncludeTypeEnums.SalesCategories,
                    IncludeTypeEnums.RiderPersona,
                    IncludeTypeEnums.LotrAppInfo
                ],
                ...(fetchOptions || {})
            };
            const customResourceLimitsColumns = entitySettings
                ? CustomColumnHelpers.getCustomResourceColumns(
                      entitySettings,
                      'riderPersonaSettings',
                      RIDER_RESOURCE_PATH
                  )
                : [];

            return (
                <RiderMaster
                    {...this.props}
                    emptyStateImageSrc="/img/masterRider-empty.svg"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'riderId'}
                    entitySettingsName={'riderSettings'}
                    fetchOptions={riderFetchOptions}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    isFieldHidden={this.areFieldsHidden}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    enableResetPassword
                    enableResetQrCode={qrEnabled}
                    enableCascadeWarning
                    customResourceLimitsColumns={customResourceLimitsColumns}
                    useLocalisation={false}
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = RiderSelectors.isEntitiesLoading(state, ownProps);
        const data = RiderSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const teams = teamsSelector(state, ownProps);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const skills = SkillSelectors.getEntities(state, ownProps);
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
        const { authSettings } = getPlatformSettings();

        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            teams,
            isImportEntityActive,
            skills,
            isExportEntityActive,
            isGlobalPersonnel,
            qrEnabled: authSettings?.qrEnabled
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        fetchSalesCategories: SalesCategoriesActions.fetchSalesCategories,
        fetchRiderPersonas: RiderPersonaActions.fetchActiveEntities,
        fetchShifts: ShiftActions.fetchActiveEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        fetchSkills: SkillActions.fetchEntities,
        bulkDisableEntity: RiderActions.bulkDisableEntity,
        disableEntity: RiderActions.disableEntity,
        resetPassword: RiderActions.resetPassword,
        bulkResetPassword: RiderActions.bulkResetPassword,
        clearData: RiderActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkResetQrCode: RiderActions.bulkResetQrCode
    });

    const enhance = compose(
        themr(null, theme),
        themr(null, specificTheme),
        withRouter,
        withUnitConverter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(RiderMasterContainer);
};

const ThemedConnectedRiderMasterContainer = riderMasterContainerFactory({});

export {
    riderMasterContainerFactory,
    ThemedConnectedRiderMasterContainer as RiderMasterContainer
};
export default ThemedConnectedRiderMasterContainer;
